import {
	buildBreadcrumbManager,
	buildFieldSortCriterion,
	buildQuerySummary,
	buildRelevanceSortCriterion,
	buildSearchParameterManager,
	buildSort,
	loadAdvancedSearchQueryActions,
	loadSearchAnalyticsActions,
	buildResultList,
	SearchEngine,
	SearchParameters,
	SortOrder
} from "@coveo/headless";
import { useMemo, useEffect, useState } from "react";
import {
	CoveoHeadlessData,
	CoveoHeadlessTemplate,
	SortByDictionary,
	CoveoHeadlessSortBy
} from "components/coveo-headless/types/coveo-headless-types";
import FacetGroup from "components/coveo-headless/components/FacetGroup";
import FacetGroupMobile from "components/coveo-headless/components/FacetGroupMobile";
import Facet from "components/coveo-headless/components/Facet";
import ImageArrow from "components/utility/images/ImageArrow";
import Sort from "components/coveo-headless/components/Sort";
import SortMobile from "components/coveo-headless/components/SortMobile";
import { generateFacets } from "components/coveo-headless/controllers/Controllers";
import deserializeUrl from "components/coveo-headless/utils/deserialize-url";
import serializeUrl from "components/coveo-headless/utils/serialize-url";
import BreadcrumbManager from "components/coveo-headless/components/BreadcrumbManager";
import TotalResults from "components/coveo-headless/components/TotalResults";
import StickyHeader from "components/utility/sticky-header/StickyHeader";
import StickyHeaderContainer from "components/utility/sticky-header/StickyHeaderContainer";
import ContentCards from "components/coveo-headless/ContentCards";
import { setBodyPositionFixed } from "utils/dom";
import setDefaultSort from "components/coveo-headless/utils/setDefaultSort";
import {
	registerField,
	registerSearchHub,
	launchAnalytics
} from "components/coveo-headless/utils/initAnalytics";
import ClientHomeLoader from "components/coveo-headless/coveo-loaders/ClientHomeLoader/ClientHomeLoader";

type ClientHomeProps = {
	engine: SearchEngine;
	jsonData: CoveoHeadlessData;
	pageType: string;
	searchHub: string;
	templates: CoveoHeadlessTemplate[];
};

const ClientHome = ({ engine, jsonData, pageType, searchHub, templates }: ClientHomeProps) => {
	const { aq, context, fields, filters, tabs, showMoreText, resultDescription, facetQuantifier } =
		jsonData;
	const { facetName } = filters;

	const facetGroupClasses = {
		container: "home-browseby-options-container",
		facetParent: "home-browseby-options-list"
	};
	const mobileFacetGroupLabel = "browse by";
	// available sort values
	const criteriaDictionary: SortByDictionary[] = [
		{
			name: "Newest",
			criterion: buildFieldSortCriterion("publishdate", SortOrder.Descending)
		},
		{
			name: "Relevancy",
			criterion: buildRelevanceSortCriterion()
		}
	];

	const defaultSort = setDefaultSort(filters.sortCriteria, criteriaDictionary);
	// string values to use for serialize-url function
	const sortByDictionary = [CoveoHeadlessSortBy.Relevancy, CoveoHeadlessSortBy.PublishDateDesc];

	const facetsMemo = useMemo(() => generateFacets(engine, facetName), [engine, facetName]);
	const breadcrumbs = useMemo(() => buildBreadcrumbManager(engine), [engine]);
	const querySummary = useMemo(() => buildQuerySummary(engine), [engine]);
	const resultList = useMemo(() => buildResultList(engine), [engine]);
	const sortCriteria = useMemo(() => buildSort(engine), [engine]);
	const [toggleMobileFacetGroup, setToggleMobileFacetGroup] = useState(false);
	const [toggleMobileFacetList, setToggleMobileFacetList] = useState(false);
	const [mobileFilterCount, setMobileFilterCount] = useState(0);
	const [mobileSelectedFacets, setMobileSelectedFacets] = useState({});
	const [selectedFacet, setSelectedFacet] = useState(0);
	const [selectedDropdown, setSelectedDropdown] = useState(-1);
	const [toggleFacets, setToggleFacets] = useState(false);
	const [toggleMobileSort, setToggleMobileSort] = useState(false);
	const [mobileSortBy, setMobileSortBy] = useState("");
	const [loading, setIsLoading] = useState(true);
	const [loaderState, setLoaderState] = useState({ isLoading: true });

	useEffect(() => {
		resultList.subscribe(() => {
			setLoaderState(resultList.state);
		});
	}, [resultList]);

	useEffect(() => {
		if (loaderState?.isLoading === false) {
			setIsLoading(false);
		}
	}, [loaderState]);

	useEffect(() => {
		const hash = window.location.hash.slice(1);
		let parameters: SearchParameters = deserializeUrl(
			aq,
			defaultSort.criteria,
			sortByDictionary,
			tabs,
			hash
		);
		if (Object.keys(parameters).length === 0) {
			parameters.aq = aq;
			parameters.sortCriteria = defaultSort.criteria;
		}
		// on page load, set sort by value for mobile
		setMobileSortBy(defaultSort.sortName);
		const searchParameterManager = buildSearchParameterManager(engine, {
			initialState: {
				parameters: {
					aq: parameters.aq,
					f: parameters.f,
					sortCriteria: parameters.sortCriteria
				}
			}
		});
		// when user filters via UI, update url hash
		searchParameterManager.subscribe(() => {
			const url = serializeUrl(
				searchParameterManager.state.parameters,
				sortCriteria.state.sortCriteria,
				pageType
			);
			// set active facets and active facet count for mobile
			if (searchParameterManager.state.parameters.f) {
				const newMobileFilterCount = Object.keys(searchParameterManager.state.parameters.f).reduce(
					(acc, val) => {
						acc += searchParameterManager.state.parameters.f![val].length;
						return acc;
					},
					0
				);
				setMobileFilterCount(newMobileFilterCount);
				setMobileSelectedFacets(searchParameterManager.state.parameters.f);
			} else {
				setMobileFilterCount(0);
				setMobileSelectedFacets({});
			}
			window.history.pushState(null, document.title, url);
		});

		/**
		 * when user types into url bar, deserialize new parameters, update UI
		 * update searchParameterManager with new query parameters
		 */
		const onHashChange = () => {
			const hash = window.location.hash.slice(1);
			const parameters = deserializeUrl(
				aq,
				defaultSort.criteria,
				sortByDictionary,
				tabs,
				decodeURIComponent(hash)
			);
			searchParameterManager.synchronize(parameters);
		};

		const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
		const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

		engine.dispatch(updateAdvancedSearchQueries({ aq: aq }));
		// this runs any searches onload of the page; is necessary, otherwise there is no aq or searchParameterManager start
		engine.executeFirstSearch(logInterfaceLoad());

		window.addEventListener("hashchange", onHashChange);

		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
		// adding all dependencies react recommends cause coveo to go into an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aq, engine, tabs]);

	launchAnalytics(engine, context);
	registerField(engine, fields);
	registerSearchHub(engine, searchHub);

	if (loading) return <ClientHomeLoader numberOfCards={Number(jsonData.filters.numberOfResults)} />;
	return (
		<>
			<div
				className={`body-mask-target ${
					toggleMobileFacetGroup || toggleMobileSort ? "body-mask" : ""
				}`}
				role="button"
				tabIndex={0}
				onClick={() => {
					setBodyPositionFixed();
					setToggleMobileFacetGroup(false);
					setToggleMobileSort(false);
					setSelectedDropdown(-1);
					setToggleMobileFacetList(false);
				}}
				onKeyDown={() => {
					setBodyPositionFixed();
					setToggleMobileFacetGroup(false);
					setToggleMobileSort(false);
					setSelectedDropdown(-1);
					setToggleMobileFacetList(false);
				}}
			></div>
			<StickyHeaderContainer>
				<StickyHeader>
					<section className="home-browseby-bar container">
						<div className="home-browseby-bar-desktop">
							<div className="home-browseby-bar-title">
								<p className="tc-cool-gray-02 t-label-md-heavy">Browse By</p>
							</div>
							<div className="home-browseby-bar-left">
								<FacetGroup
									classes={facetGroupClasses}
									facets={facetsMemo}
									fields={facetName}
									page={pageType}
									toggle={toggleFacets}
									setSelectedFacet={setSelectedFacet}
									setToggle={setToggleFacets}
								/>
							</div>
							<div className="home-browseby-bar-right">
								<div className="home-browseby-bar-sort-container">
									<Sort controller={sortCriteria} criteria={criteriaDictionary} />
								</div>
							</div>
							<div className={`home-browseby-options-sublist ${toggleFacets ? "" : "hide"}`}>
								<Facet
									facet={facetsMemo[selectedFacet]}
									facetGroup={filters.facetName[selectedFacet].name}
								/>
							</div>
						</div>
						<div className="home-browseby-bar-mobile">
							<div className="home-browseby-bar-mobile-title">
								<h3>Browse and Explore</h3>
							</div>
							<div className="home-browseby-bar-mobile-filters">
								<div className="home-browseby-bar-mobile-filter-button">
									<div
										aria-label="browseby filter"
										className="home-browseby-bar-mobile-browseby"
										role="button"
										tabIndex={0}
										onClick={() => {
											setBodyPositionFixed();
											setToggleMobileFacetGroup(!toggleMobileFacetGroup);
										}}
										onKeyDown={() => {
											setBodyPositionFixed();
											setToggleMobileFacetGroup(!toggleMobileFacetGroup);
										}}
									>
										<div>Browse By</div>
										<div
											className={`home-browseby-bar-mobile-filter-count ${
												mobileFilterCount ? "show" : ""
											}`}
										>
											{mobileFilterCount === 0 ? null : mobileFilterCount}
										</div>
									</div>
								</div>
								<div className="home-browseby-bar-mobile-filter-button">
									<div
										aria-label="sortby filter"
										className="home-browseby-bar-mobile-sortby"
										role="button"
										tabIndex={0}
										onClick={() => {
											setBodyPositionFixed();
											setToggleMobileSort(!toggleMobileSort);
										}}
										onKeyDown={() => {
											setBodyPositionFixed();
											setToggleMobileSort(!toggleMobileSort);
										}}
									>
										<div className="home-browseby-bar-mobile-sortby-label">{mobileSortBy}</div>
										<ImageArrow />
									</div>
								</div>
							</div>
						</div>
					</section>
				</StickyHeader>
			</StickyHeaderContainer>
			{toggleMobileFacetGroup && (
				<div
					className={`coveo-headless-mobile-facet-group ${
						toggleMobileFacetGroup ? "coveo-headless-mobile-facet-group-show" : ""
					} ${toggleMobileFacetList ? "coveo-headless-mobile-facet-group-list-open" : ""}`}
				>
					<FacetGroupMobile
						breadcrumbs={breadcrumbs}
						facets={facetsMemo}
						fields={facetName}
						mobileFacetGroupLabel={mobileFacetGroupLabel}
						mobileFilterCount={mobileFilterCount}
						mobileSelectedFacets={mobileSelectedFacets}
						pageType={pageType}
						toggleMobileFacetList={toggleMobileFacetList}
						selectedDropdown={selectedDropdown}
						setSelectedDropdown={setSelectedDropdown}
						setToggleMobileFacetGroup={setToggleMobileFacetGroup}
						setToggleMobileFacetList={setToggleMobileFacetList}
						facetQuantifier={facetQuantifier}
					>
						<TotalResults
							controller={querySummary}
							showInFilters={true}
							pageType={pageType}
							totalResultsDescription={resultDescription}
						/>
					</FacetGroupMobile>
				</div>
			)}
			{toggleMobileSort && (
				<div
					className={`coveo-headless-mobile-tab-section ${
						toggleMobileSort ? "coveo-headless-mobile-tab-section-show" : ""
					}`}
				>
					<SortMobile
						controller={sortCriteria}
						criteria={criteriaDictionary}
						setMobileSortBy={setMobileSortBy}
						setToggleMobileSort={setToggleMobileSort}
					/>
				</div>
			)}
			<div className="container">
				<div className="home-browseby-breadcrumbs-container grid">
					<TotalResults
						controller={querySummary}
						showInFilters={false}
						pageType={pageType}
						totalResultsDescription={resultDescription}
					/>
					<BreadcrumbManager controller={breadcrumbs} page={pageType} />
				</div>
				<div className="coveo-headless-content-cards-wrapper">
					<ContentCards
						controller={resultList}
						headlessEngine={engine}
						showMoreText={showMoreText}
						templates={templates}
					/>
				</div>
			</div>
		</>
	);
};

export default ClientHome;
